import { hasS3Object } from "@/lib/aws-amplify/storage";

import { Converter } from "@/components/features/_shared";
import { convertRequiredPrepMillisecondIntoRequiredPrepMillisecondBeforeDateAndHour } from "@/components/features/_shared/validation-schema/converter";

import { DUMMY_IMAGE } from "@/constants/dummy-image";

import type { ProductPlanFormModel } from "../../../../../type";
import type { ServerModel } from "../../type";

export const serverModelToInputModelConverter = (serverModel: ServerModel): ProductPlanFormModel["InputModel"] => {
	const { planNewItem, hotelPolicy, customEmailSetting } = serverModel;

	// 初期値が他の設定に依存する可能性がある項目は、ここで初期値を設定する
	const emailTemplateReservation = {
		atCanceled:
			planNewItem.emailTemplateReservation?.atCanceled ?? customEmailSetting?.reservation?.atCanceled ?? "",
		atCin: planNewItem.emailTemplateReservation?.atCin ?? customEmailSetting?.reservation?.atCin ?? "",
		atCreated: planNewItem.emailTemplateReservation?.atCreated ?? customEmailSetting?.reservation?.atCreated ?? "",
		atUpdated: planNewItem.emailTemplateReservation?.atUpdated ?? customEmailSetting?.reservation?.atUpdated ?? "",
	};
	const checkInStart = planNewItem.checkInStart ?? hotelPolicy?.checkInStart ?? "";
	const checkInEnd = planNewItem.checkInEnd ?? hotelPolicy?.checkInEnd ?? "";
	const checkOut = planNewItem.checkOut ?? hotelPolicy?.checkOut ?? "";

	const requiredPrepMillisecond = planNewItem.requiredPrepMillisecond || hotelPolicy?.requiredPrepMillisecond || 0;
	const { requiredPrepMillisecondBeforeDate, requiredPrepMillisecondBeforeHour } =
		convertRequiredPrepMillisecondIntoRequiredPrepMillisecondBeforeDateAndHour(requiredPrepMillisecond);

	const inputModel: ProductPlanFormModel["InputModel"] = {
		hotelID: planNewItem.hotelID,
		planID: planNewItem.planID,

		name: planNewItem.name,
		mainTarget: planNewItem.mainTarget,

		keyVisuals: planNewItem.keyVisuals,
		mainKeyVisual: hasS3Object(planNewItem.mainKeyVisual) ? planNewItem.mainKeyVisual : DUMMY_IMAGE.s3Object,

		availableDiscountIDs: planNewItem.availableDiscountIDs,
		availableOptionIDs: planNewItem.availableOptionIDs,
		availableRoomIDs: planNewItem.availableRoomIDs,

		billingTypeTarget: planNewItem.billingTypeTarget,
		billingTypeTerm: planNewItem.billingTypeTerm,

		checkInAvailableDayOfTheWeek: planNewItem.checkInAvailableDayOfTheWeek ?? [],

		checkInEnd,
		checkInStart,
		checkOut,

		coSleepingKidsPrice: planNewItem.coSleepingKidsPrice,

		defaultDiscountID: planNewItem.defaultDiscountID,

		doNotCountKidsAsStock: planNewItem.doNotCountKidsAsStock,

		emailTemplateReservation,

		inSale: planNewItem.inSale,
		isChangeOnlyOption: planNewItem.isChangeOnlyOption,
		isCoSleepingKidsPriceActive: planNewItem.isCoSleepingKidsPriceActive,
		includedMeals: planNewItem.includedMeals,
		isDayTrip: planNewItem.isDayTrip,
		isHiddenHotelQuestions: planNewItem.isHiddenHotelQuestions,
		isNoAddressNeeded: planNewItem.isNoAddressNeeded,
		isPrivate: planNewItem.isPrivate,
		isRequest: planNewItem.isRequest,
		isRoomPriceFree: planNewItem.isRoomPriceFree,
		isSCConnected: planNewItem.isSCConnected,
		isShowRoomForGuest: planNewItem.isShowRoomForGuest,
		isUncancelable: planNewItem.isUncancelable,
		isUnchangeable: planNewItem.isUnchangeable,
		notSendCinMail: planNewItem.notSendCinMail,
		roomChangeDisable: planNewItem.roomChangeDisable,

		kidsPolicy: planNewItem.kidsPolicy?.map(Converter.kidsPolicyItemConverter),
		cancelPolicy: planNewItem.cancelPolicy,

		longStayParams: planNewItem.longStayParams,

		maxDailyStockNum: planNewItem.maxDailyStockNum,
		maxStockNum: planNewItem.maxStockNum,

		minimumPeopleNumPerRoom: planNewItem.minimumPeopleNumPerRoom ?? 1,
		maxPeopleNumPerRoom: planNewItem.maxPeopleNumPerRoom,

		minRoomNum: planNewItem.minRoomNum ?? 1,
		maxRoomNum: planNewItem.maxRoomNum,

		onlyUseCreditPayment: planNewItem.onlyUseCreditPayment,
		onlyUseLocalPayment: planNewItem.onlyUseLocalPayment,

		price: planNewItem.price,

		questions: planNewItem.questions,

		requiredOptionIDs: planNewItem.requiredOptionIDs,

		requiredOptionGroupIDs: planNewItem.requiredOptionGroupIDs,

		requiredPrepMillisecond,

		saleStart: planNewItem.saleStart,
		saleEnd: planNewItem.saleEnd,
		saleStartTime: planNewItem.saleStartTime,
		saleEndTime: planNewItem.saleEndTime,
		saleStartBeforeDate: planNewItem.saleStartBeforeDate,

		saleTypeForTerm: planNewItem.saleTypeForTerm,

		shortStayParams: planNewItem.shortStayParams,

		staticPriceParams: planNewItem.staticPriceParams,

		stayableDayOfTheWeek: planNewItem.stayableDayOfTheWeek ?? [],
		stayableEnd: planNewItem.stayableEnd,
		stayableStart: planNewItem.stayableStart,

		stockTypeTarget: planNewItem.stockTypeTarget,
		stockTypeTerm: planNewItem.stockTypeTerm,

		// InputModel 固有のプロパティ
		requiredPrepMillisecondBeforeDate,
		requiredPrepMillisecondBeforeHour,
		atCreated: {
			text: "",
			override: false,
		},
		atUpdated: {
			text: "",
			override: false,
		},
		atCanceled: {
			text: "",
			override: false,
		},
		atCheckInBefore_00days: {
			text: "",
			shouldSendEmail: false,
			override: false,
		},
		atCheckInBefore_01days: {
			text: "",
			shouldSendEmail: false,
			override: false,
		},
		atCheckInBefore_07days: {
			text: "",
			shouldSendEmail: false,
			override: false,
		},
		atCheckOutAfter_01days: {
			text: "",
			shouldSendEmail: false,
			override: false,
		},
	} satisfies ProductPlanFormModel["InputModel"];

	return inputModel;
};
